import React, { useContext } from 'react';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { useGetCaseDetails } from '../../api/casedetails';
import UserProfileContext from '../../context/userprofilecontext.js';
import { formatDate, formatCurrency } from '../../utils/formatters.js';
import { addFootersToAllPages, normalizeMargin, standardPDFStyles, addFiltersDescription, addCenteredDescriptionBlock, addPageIfNeeded } from '../shared/PDFCommon.js';
import { handlePDFPrintChart } from './sankey-chart-component.js';
import { deriveCardholderName, deriveCardEnding } from '../../utils/cardholder.js';

// Width the Cardholder column takes when shown; the text columns of the active
// layout shrink in proportion to make room, so the page width is unchanged.
const CARDHOLDER_CELL_WIDTH = 64;
const SHRINKABLE_COLUMNS = ['bankAccountName', 'description', 'merchantName', 'subcategoryName', 'flagName'];

const withCardholderColumn = (widths, displayOptions) => {
    const shown = SHRINKABLE_COLUMNS.filter((key) => key === 'bankAccountName' || key === 'description'
        || (key === 'merchantName' && displayOptions.showMerchant)
        || (key === 'subcategoryName' && displayOptions.showSubcategory)
        || (key === 'flagName' && displayOptions.showFlag));
    const total = shown.reduce((sum, key) => sum + widths[key], 0);
    const scale = (total - CARDHOLDER_CELL_WIDTH) / total;
    const scaled = { ...widths, cardholderName: CARDHOLDER_CELL_WIDTH };
    shown.forEach((key) => { scaled[key] = widths[key] * scale; });
    return scaled;
};

// "HOLDER B, ending 2010" / "Account-level" — the PDF cell is one string.
export const formatCardholderForPDF = (cardView) => {
    const ending = deriveCardEnding(cardView);
    const name = deriveCardholderName(cardView);
    return ending ? `${name}, ending ${ending}` : name;
};

const getAccountTableHeaders = (displayOptions) => {



    let configuration = "base";
    if (displayOptions.showMerchant) {
        configuration += "_merchant";
    }
    if (displayOptions.showSubcategory) {
        configuration += "_subcategory";
    }
    if (displayOptions.showFlag) {
        configuration += "_flag";
    }

    const cellWidths = {
        base: {
            index: 18,
            transactionDate: 35,
            bankAccountName: 108,
            description: 253,
            amount: 56,
            merchantName: 58,
            subcategoryName: 72,
            flagName: 72
        },
        base_merchant: {
            index: 18,
            transactionDate: 35,
            bankAccountName: 94,
            description: 202,
            amount: 49,
            merchantName: 72,
            subcategoryName: 72,
            flagName: 72
        },
        base_merchant_subcategory: {
            index: 18,
            transactionDate: 35,
            bankAccountName: 79,
            description: 173,
            amount: 42,
            merchantName: 65,
            subcategoryName: 58,
            flagName: 72
        },
        base_merchant_subcategory_flag: {
            index: 18,
            transactionDate: 35,
            bankAccountName: 54, // Reduced from 150
            description: 137, // Increased from 160
            amount: 42,
            merchantName: 51, // Slightly reduced from 75
            subcategoryName: 61, // Increased from 70
            flagName: 72 // Increased from 65
        },
        base_merchant_flag: {
            index: 18,
            transactionDate: 35,
            bankAccountName: 79,
            description: 181,
            amount: 49,
            merchantName: 65,
            subcategoryName: 72,
            flagName: 43
        },
        base_subcategory: {
            index: 18,
            transactionDate: 35,
            bankAccountName: 101,
            description: 209,
            amount: 49,
            merchantName: 58,
            subcategoryName: 58,
            flagName: 72
        },
        base_subcategory_flag: {
            index: 18,
            transactionDate: 35,
            bankAccountName: 79,
            description: 181,
            amount: 49,
            merchantName: 58,
            subcategoryName: 58,
            flagName: 51
        },
        base_flag: {
            index: 18,
            transactionDate: 35,
            bankAccountName: 94,
            description: 217,
            amount: 49,
            merchantName: 58,
            subcategoryName: 72,
            flagName: 58
        }
    }

    const widths = displayOptions?.showCardholder
        ? withCardholderColumn(cellWidths[configuration], displayOptions)
        : cellWidths[configuration];

    const defaultColumnStyle = {}
    // const defaultCurrencyStyle = { halign: "right", columnStyle: { cellWidth: 25 } };

    let headers = [
        { header: "#", dataKey: "index", columnStyle: { ...defaultColumnStyle, cellWidth: widths.index, halign: "center" } },
        { header: "Date", dataKey: "transactionDate", columnStyle: { ...defaultColumnStyle, cellWidth: widths.transactionDate, halign: "center" } },
        { header: "Account", dataKey: "bankAccountName", columnStyle: { ...defaultColumnStyle, cellWidth: widths.bankAccountName, halign: "left" } },
        ...(displayOptions?.showCardholder
            ? [{ header: "Cardholder", dataKey: "cardholderName", columnStyle: { ...defaultColumnStyle, cellWidth: widths.cardholderName, halign: "left" } }]
            : []),
        { header: "Description", dataKey: "description", columnStyle: { ...defaultColumnStyle, cellWidth: widths.description, halign: "left" } },
        { header: "Amount", dataKey: "amount", columnStyle: { ...defaultColumnStyle, cellWidth: widths.amount, halign: "right" } },



    ];
    if (displayOptions?.showMerchant) {
        headers.push(
            { header: "Merchant", dataKey: "merchantName", columnStyle: { ...defaultColumnStyle, cellWidth: widths.merchantName, halign: "left" } },
        );
    }
    if (displayOptions?.showSubcategory) {
        headers.push(
            { header: "Subcategory", dataKey: "subcategoryName", columnStyle: { ...defaultColumnStyle, cellWidth: widths.subcategoryName, halign: "left" } },
        );
    }
    if (displayOptions?.showFlag) {
        headers.push(
            { header: "Flag", dataKey: "flagName", columnStyle: { ...defaultColumnStyle, cellWidth: widths.flagName, halign: "left" } }
        );
    }

    return headers;
};

const calculateAccountTableColumnStyles = (headers) => {
    let columnStyles = {};
    headers.forEach((header, index) => {
        columnStyles[index] = header.columnStyle || {};
    });
    return columnStyles;
};

const usePDFPrintTransactions = ({ transactions, chartRef, displayOptions, filtersDescription, sortDescription }) => {
    console.log('PDFReportGenerator transactions:', transactions);
    const { data: caseDetails } = useGetCaseDetails();
    const userProfileContext = useContext(UserProfileContext);
    const currentCase = userProfileContext?.userProfile?.currentCase || {};
    const currentOrg = userProfileContext?.userProfile?.currentOrg || {};

    return async () => {

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: 'letter'
        });

        // Page dimensions for landscape letter (792 x 612 pt)
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 72;
        const contentWidth = pageWidth - (margin * 2);

        let yPosition = normalizeMargin(margin).top;

        // Calculate totals
        const totalTransactions = transactions.length;
        const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);

        const addTitle = (doc, pageWidth, yPosition) => {
            // Title
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            const title = currentCase.caseName || "Petitioner v. Respondent";
            doc.text(title, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 16;

            // Court info
            if (caseDetails?.courtJurisdiction) {
                doc.setFontSize(11);
                doc.setFont(undefined, 'normal');
                const courtInfo = `${caseDetails?.courtJurisdiction}${caseDetails?.jurisdictionState ? `, ${caseDetails?.jurisdictionState}` : ''}`;
                doc.text(courtInfo, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += 14;
            }

            // Case number
            if (caseDetails?.caseNumber) {
                doc.setFontSize(11);
                doc.text(`Civil Action File No.: ${caseDetails?.caseNumber}`, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += 14;
            }

            // Main title
            yPosition += 12;
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('Transaction Details', pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 16;

            // Marriage date
            if (caseDetails?.marriageDate) {
                doc.setFontSize(11);
                doc.setFont(undefined, 'normal');
                doc.text(`Date of Marriage: ${formatDate(new Date(caseDetails?.marriageDate))}`, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += 14;
            }

            // Purpose statement
            if (currentCase.caseName) {
                doc.setFontSize(11);
                doc.text(`For litigation purposes only in the matter of ${currentCase.caseName}`, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += 20;
            } else {
                yPosition += 20;
            }

            doc.setFontSize(11);
            doc.text(`Total Transactions: ${totalTransactions}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 20;
            doc.text(`Total Amount: $${formatCurrency(totalAmount)}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 20;

            return yPosition;
        };


        yPosition = addTitle(doc, pageWidth, yPosition);

        yPosition = addFiltersDescription(doc, filtersDescription, margin, yPosition);

        // Rows are printed in the exact order the viewer showed them, so the
        // report has to say what that order was -- otherwise a layered sort
        // reads as an arbitrary sequence to whoever receives the printout.
        if (sortDescription) {
            yPosition = addCenteredDescriptionBlock(doc, 'Sort Order:', [sortDescription], margin, yPosition);
        }


        if (chartRef && displayOptions.printChart) {
            yPosition = await handlePDFPrintChart(doc, margin, yPosition, chartRef);
        }

        const formatTransactionTableData = (rowIndex, transactions, displayOptions) => {
            const mapTransactionToListItem = (transaction, rowType, displayOptions) => {
                // console.log("Mapping transaction to list item, mapTransferPartner:", mapTransferPartner, ", input:");
                // console.log(JSON.stringify(transaction, null, 2));

                const result = {
                    rowType: rowType,
                    index: rowIndex,
                    bankAccountName: transaction.bankAccount?._displayName,
                    cardholderName: formatCardholderForPDF(transaction.card_view),
                    transactionDate: formatDate(transaction.transaction?.posting_date),
                    description: transaction.transaction?.description,
                    amount: formatCurrency(transaction.transaction?.amount),
                    merchantName: transaction.merchant?.name || 'Unknown Merchant',
                    merchantLocation: transaction.merchantCity?.name || 'Unknown Location',
                    associatedNames: transaction.associatedNames?.map(name => name.name) || [],
                    tripLocations: transaction.tripLocations?.map(location => location.name) || [],
                    subcategoryName: transaction.subcategory?.name || 'Uncategorized',
                    categoryName: transaction.category?.name || 'Uncategorized',
                    flagName: transaction.flag?.name,
                };
                rowIndex++;
                return [result, rowIndex];
            };

            const formattedData = [];


            transactions.forEach((transaction) => {
                let row;
                let rowType = transaction.hasTransferPartner ? "transfer_from" : "transaction";
                [row, rowIndex] = mapTransactionToListItem(transaction, rowType, displayOptions);
                formattedData.push(row);
                if (transaction.hasTransferPartner) {
                    rowType = "transfer_to";
                    let transferToRow;
                    [transferToRow, rowIndex] = mapTransactionToListItem(transaction.transfer_partner, rowType, displayOptions);
                    formattedData.push(transferToRow);
                }
            });
            return [formattedData, rowIndex];
        };

        const formatRow = (data) => {
            const rowType = data.row.raw.rowType   /// get the original data value of rowType

            if (rowType === "transfer_to") {
                if (data.row.section === 'body') {
                    if (data.column.dataKey === 'transactionDate') {
                        data.cell.text = `  ${data.cell.text}`
                    }
                    data.cell.styles.fontStyle = standardPDFStyles.table.body.transfer_to.fontStyle;
                    data.cell.styles.textColor = standardPDFStyles.table.body.transfer_to.textColor;
                }
            }


            if (data.row.section === 'body' && data.column.dataKey === 'index') {
                data.cell.styles.fontStyle = standardPDFStyles.table.body.index_column.fontStyle;
            }

        };



        if (displayOptions.printTransactions) {
            const accountTableHeaders = getAccountTableHeaders(displayOptions);
            const accountTableColumnStyles = calculateAccountTableColumnStyles(accountTableHeaders);
            const cellFontSize = 5;

            let rowIndex = 1;
            let formattedTransactionData;
            [formattedTransactionData, rowIndex] = formatTransactionTableData(rowIndex, transactions, displayOptions);

            yPosition = addPageIfNeeded(doc, margin, yPosition);
            // //Old autoTable call
            autoTable(doc, {
                columns: accountTableHeaders,
                // head: [['#', 'Date', 'Account', 'Description', 'Amount', 'Category', 'Flags']],
                body: formattedTransactionData,
                startY: yPosition,
                //theme: 'grid', //Tukta took this off.
                //margin: { top: normalizeMargin(margin).top, left: normalizeMargin(margin).left, right: normalizeMargin(margin).right, bottom: normalizeMargin(margin).bottom },
                // margin: {
                //     top: normalizeMargin(margin).top,
                //     left: normalizeMargin(margin).left,
                //     right: normalizeMargin(margin).right,
                //     bottom: normalizeMargin(margin).bottom
                // },
                styles: {
                    // fontSize: 9,
                    // cellPadding: 5,
                    // lineColor: [0, 0, 0],
                    // lineWidth: 0.5,
                    // textColor: [0, 0, 0],
                    // overflow: 'linebreak',
                    // font: 'times'
                    overflow: 'linebreak',
                    cellWidth: 'auto',
                    font: 'times',
                    fontSize: cellFontSize,
                    valign: 'middle',
                },
                headStyles: {
                    // fillColor: [240, 240, 240],
                    // textColor: [0, 0, 0],
                    // fontStyle: 'bold',
                    // fontSize: 10,
                    // lineColor: [0, 0, 0],
                    // lineWidth: 1
                    fillColor: standardPDFStyles.table.header.default.fillColor,
                    textColor: standardPDFStyles.table.header.default.textColor,
                    halign: 'center'
                },

                // columnStyles: {
                //     0: { cellWidth: 25, halign: 'center' },   // # - Minimal reference number
                //     1: { cellWidth: 60, halign: 'center' },   // Date - Centered, adequate for MM/DD/YYYY
                //     2: { cellWidth: 80, halign: 'left' },     // Account - Left aligned, compact
                //     3: { cellWidth: 170, halign: 'left' },    // Description - Priority field, left aligned
                //     4: { cellWidth: 70, halign: 'right' },    // Amount - Right aligned (accounting standard)
                //     5: { cellWidth: 70, halign: 'left' },     // Merchant - Left aligned, moderate space
                //     6: { cellWidth: 75, halign: 'left' },     // Subcategory - Left aligned, compact
                //     7: { cellWidth: 100, halign: 'left' }    // Flag - Centered, adequate for status
                // },
                // alternateRowStyles: {
                //     fillColor: [250, 250, 250]
                // },

                columnStyles: accountTableColumnStyles,

                margin: {
                    top: normalizeMargin(margin).top,
                    left: normalizeMargin(margin).left,
                    right: normalizeMargin(margin).right,
                    bottom: normalizeMargin(margin).bottom
                },


                // columnStyles: accountTableColumnStyles,
                // didParseCell: formatRow,
                // columnStyles: {
                //     0: { cellWidth: 25, halign: 'center' },   // Transaction # - Center
                //     1: { cellWidth: 65, halign: 'center' },   // Date - Center
                //     2: { cellWidth: 100, halign: 'left' },    // Account - Left
                //     3: { cellWidth: 200, halign: 'left' },    // Description - Left
                //     4: { cellWidth: 80, halign: 'right' },    // Amount - Right (Accounting Standard)
                //     5: { cellWidth: 90, halign: 'left' },     // Category - Left
                //     6: { cellWidth: 130, halign: 'center' }   // Flags - Center
                // },
                // didParseCell: function (data) {
                //     const rowIndex = data.row.index;
                //     const colIndex = data.column.index;
                //     const transaction = transactions[rowIndex];

                didParseCell: function (data) {
                    formatRow(data);
                }
            });
        }




        addFootersToAllPages(doc, currentOrg, {
            isDraft: displayOptions.isDraft
        }, margin);

        // Save the PDF with professional filename
        const timestamp = new Date().toISOString().slice(0, 10);
        doc.save(`Financial_Transaction_Analysis_${timestamp}.pdf`);

    };
};

export default usePDFPrintTransactions;